<template>
  <v-container fluid>
   <Alert13/>
    <v-alert v-if="isInitialLoadingUnfinished" text type="warning">
      {{ $t('positions-not-updated') }}
      <a href="#/account/subscriptions">{{ $t('subscribe-for-positions') }}</a>
    </v-alert>

    <v-alert v-if="remainingKeywordsCount < 0" text type="error">
      {{ $t('subscription-limit-reached', {count: $account.current.rules['check-position'].max_monitor}) }}
      <a href="#/account/subscriptions">{{ $t('subscribe-now') }}</a>

    </v-alert>

    <v-row no-gutters>
      <v-col cols="12" sm="6" xs="6">
        <div class="mt-2 mb-3">
          <div class="text-h6">{{ $t('title') }}
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="grey" dark small v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
              </template>
              <span>{{ $t('tooltip') }}</span>
            </v-tooltip>
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                    :color="remainingKeywordsCount <= 10 ? 'error' : (remainingKeywordsCount < 50 ? 'warning' : 'success')"
                    class="ma-2"
                    label
                    outlined
                    small
                    to="/account/subscriptions"
                    v-bind="attrs"
                    v-on="on">
                  {{ $services.monitoring.monitors.all.length }} /
                  {{ $account.current.rules['check-position'].max_monitor }}
                </v-chip>
              </template>
              <span>
                {{ $t('add-keywords') }}
                {{ $account.current.rules['check-position'].max_monitor }}
                {{ $t('increase-keywords') }}
              </span>
            </v-tooltip>
          </div>
          <div v-if="false" style="color: rgba(0, 0, 0, 0.6); font-size: 12px; margin-top: -6px;">
            {{ layout.name }}
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" xs="6">
        <date-picker
            :class="$vuetify.breakpoint.smAndUp ? 'mt-3 text-right' : 'mb-3'"
            :comparisonDate="datepicker.comparison"
            :currentDate="datepicker.current"
            @changed="handleDatePickerChanged"
        ></date-picker>
      </v-col>
    </v-row>

    <a-action-buttons-bar
        :buttons="buttons"
        :options="{ date, selected }"
        class="mb-3">
    </a-action-buttons-bar>

    <v-row>
      <v-col cols="12" md="6">
        <page-search :search="search" @changed="onSearch"></page-search>
      </v-col>
      <v-col cols="12" md="6">
        <page-group :group="group" @changed="onGroup"></page-group>
      </v-col>
    </v-row>

    <page-table
        :date="date"
        :group="group"
        :monitors="monitors"
        :reports="reports"
        :search="search"
        @search="onSearch"
        @select="onSelect"
    >
    </page-table>

    <add-keywords-dialog></add-keywords-dialog>
    <choose-day-report-dialog></choose-day-report-dialog>
    <keyword-detail-dialog></keyword-detail-dialog>
    <refresh-keywords-dialog></refresh-keywords-dialog>
    <remove-keywords-dialog></remove-keywords-dialog>
    <manage-exports-dialog></manage-exports-dialog>
    <manage-notes-dialog></manage-notes-dialog>
    <manage-tags-dialog></manage-tags-dialog>
    <update-tags-dialog></update-tags-dialog>
  </v-container>
</template>

<script>
import DatePicker from '@/views/Pages/PositionMonitoring/Components/Common/DatePicker'
import ChooseDayReportDialog from '@/views/Pages/PositionMonitoring/Dialogs/ChooseDayReportDialog'
import KeywordDetailDialog from '@/views/Pages/PositionMonitoring/Dialogs/KeywordDetailDialog/KeywordDetailDialog'
import RefreshKeywordsDialog from '@/views/Pages/PositionMonitoring/Dialogs/RefreshKeywordsDialog'
import RemoveKeywordsDialog from '@/views/Pages/PositionMonitoring/Dialogs/RemoveKeywordsDialog'
import AddKeywordsDialog from '@/views/Pages/PositionMonitoring/Dialogs/AddKeywordDialog/AddKeywordsDialog'
import PageSearch from '@/views/Pages/PositionMonitoring/Components/PageSearch'
import PageTable from '@/views/Pages/PositionMonitoring/Components/PageTable'
import ManageNotesDialog from '@/views/Pages/PositionMonitoring/Dialogs/ManageNotesDialog'
import ManageTagsDialog from '@/views/Pages/PositionMonitoring/Dialogs/ManageTagsDialog'
import PageGroup from '@/views/Pages/PositionMonitoring/Components/PageGroup'
import UpdateTagsDialog from '@/views/Pages/PositionMonitoring/Dialogs/UpdateTagsDialog'
import ManageExportsDialog from '@/views/Pages/PositionMonitoring/Dialogs/ManageExportsDialog/ManageExportsDialog'
import Alert13 from "@/plugins/common/account/Components/Shared/Alert13.vue";


export default {
  name: 'MonitoringLayoutPage',
  components: {
    Alert13,
    ManageExportsDialog,
    UpdateTagsDialog,
    PageGroup,
    ManageTagsDialog,
    ManageNotesDialog,
    PageTable,
    PageSearch,
    AddKeywordsDialog,
    RemoveKeywordsDialog,
    RefreshKeywordsDialog,
    KeywordDetailDialog,
    ChooseDayReportDialog,
    DatePicker
  },
  data() {
    return {
      persist: ['group'],
      datepicker: {
        current: this.$moment().clone().format('YYYY-MM-DD 00:00'),
        comparison: this.$moment().clone().subtract(1, 'day').format('YYYY-MM-DD 00:00'),
      },
      selected: [],
      search: '',
      group: 'domain',
      buttons: [
        {
          color: 'success',
          text: this.$t('actions.add'),
          icon: 'mdi-plus',
          click: () => this.$ui.openDialog('add_keywords'),
        },
        {
          color: 'error',
          text: ({selected}) => this.$t('actions.remove') + ' (' + selected.length + ')',
          icon: 'mdi-delete',
          disabled: ({selected}) => selected.length === 0,
          click: ({selected}) => this.$ui.openDialog('remove_keywords', {ids: selected}),
        },
        {
          color: 'warning',
          text: ({selected}) => selected.length === 0 ? this.$t('actions.refresh') : this.$t('actions.refresh') + ' (' + selected.length + ')',
          icon: 'mdi-refresh',
          disabled: () => this.isInitialLoadingUnfinished,
          click: ({selected}) => this.$ui.openDialog('refresh_keywords', {ids: selected}),
        },
        {
          color: 'primary',
          text: ({selected}) => selected.length === 0 ? this.$t('actions.exports') : this.$t('actions.create-export') + ' (' + selected.length + ')',
          icon: 'mdi-download',
          click: ({date, selected}) => this.$ui.openDialog('manage_exports', {date, ids: selected}),
        },
        {
          color: 'primary',
          text: ({selected}) => selected.length === 0 ? this.$t('manage-tags') : this.$t('edit-tags') + ' (' + selected.length + ')',
          icon: 'mdi-tag-text-outline',
          click: ({selected}) => selected.length === 0 ? this.$ui.openDialog('manage_tags') : this.$ui.openDialog('update_tags', {ids: selected}),
        },
        {
          color: 'primary',
          text: this.$t('notes'),
          icon: 'mdi-comment-text-multiple-outline',
          click: () => this.$ui.openDialog('manage_notes'),
        },
      ]
    }
  },
  computed: {
    date() {
      return this.$services.monitoring.date
    },
    reports() {
      return this.$services.monitoring.computedReports
    },
    monitors() {
      return this.$services.monitoring.monitors.all
    },
    isInitialLoadingUnfinished() {
      // If the user has a subscription, do now show the message
      if (this.$account.getSubscription('check-position').name !== 'default') {
        return false
      }

      // If we are not looking the first sample of the day
      if (!this.datepicker.current.includes('00:00')) {
        return false
      }

      return this.monitors.map(row => {
        let position = undefined

        // If there is a report with samples for today
        if (this.reports.current) {
          // If the report includes the id of the monitor
          if (row.id in this.reports.current.monitors) {
            // If the selected sample is on the list of positions of the monitor
            if (this.$services.monitoring.computedCurrentSample in this.reports.current.monitors[row.id]) {
              position = this.reports.current.monitors[row.id][this.$services.monitoring.computedCurrentSample]
            }
          }
        }

        return position
      }).filter(position => position === null).length > 0
    },
    remainingKeywordsCount() {
      return this.$account.current.rules['check-position'].max_monitor - this.$services.monitoring.monitors.all.length
    }
  },
  watch: {
    datepicker: {
      deep: true,
      handler({current, comparison}) {
        this.$services.monitoring.setReportsDates({current, comparison})
      }
    }
  },
  methods: {
    handleDatePickerChanged({current, comparison}) {
      this.datepicker.current = current
      this.datepicker.comparison = comparison
    },
    onSearch(search) {
      this.search = search
    },
    onGroup(group) {
      this.group = group
    },
    onSelect(selected) {
      this.selected = selected
    },
  },
  async mounted() {
    this.$services.monitoring.setReportsDates(this.datepicker)
    this.$bus.on('monitoring/change_date', ({current, comparison}) => {
      this.datepicker.current = current
      this.datepicker.comparison = comparison
    })
  }
}
</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "add-keywords": "You can add up to ",
    "edit-tags": "Edit Tags",
    "increase-keywords": "keywords. Click here to increase this limit.",
    "manage-tags": "Tags",
    "notes": "Notes",
    "subscribe-now": "Subscribe to a higher offer to increase this limit",
    "subscription-limit-reached": "Your account contains more keywords than the limit allowed by your subscription. The list below was truncated to {count} results. Without updating your subscription, additional keywords will be permanently deleted within 7 days.",
    "title": "Position tracking",
    "tooltip": "This tool allows you to follow day after day the evolution of your websites on a list of keywords.",
    "positions-not-updated": "Your daily positions are not yet updated (it can take a few minutes up to a few hours).",
    "subscribe-for-positions": "Subscribe now for a priority update of your positions",
    "new-pricing-note": "<strong>Our subscriptions are changing</strong> : on december 12, 2022 the starter offer of 200 free keywords will be replaced by a trial offer of 10 keywords. To keep your keywords on this date ",
    "new-pricing-link": "subscribe to an offer of {count} keywords",
    "new-pricing-supp": "at {price} €/month (for a first annual payment using the BYEBETA coupon)."
  },
  "es": {
    "add-keywords": "Puedes agregar hasta ",
    "edit-tags": "Editar etiquetas",
    "increase-keywords": "palabras clave. Haga clic aquí para aumentar su límite.",
    "manage-tags": "Etiquetas",
    "notes": "Notas",
    "subscribe-now": "Suscríbete a una oferta superior para aumentar este límite",
    "subscription-limit-reached": "Su cuenta contiene más palabras clave que el límite permitido por su suscripción. La siguiente lista se truncó a {count} resultados. Sin actualizar su suscripción, las palabras clave adicionales se eliminarán de forma permanente dentro de los 7 días.",
    "title": "Seguimiento de posición",
    "tooltip": "Esta herramienta te permite seguir día tras día la evolución de tus sitios web en una lista de palabras clave.",
    "positions-not-updated": "Tus posiciones del día aún no se han actualizado (puede tardar desde unos minutos hasta unas horas).",
    "subscribe-for-positions": "Suscríbete para beneficiarte de una actualización prioritaria de tus posiciones",
    "new-pricing-note": "<strong>Nuestras tarifas están cambiando</strong> : el 12 de diciembre de 2022, la oferta de lanzamiento de 200 palabras clave gratuitas será reemplazada por una oferta de prueba de 10 palabras clave. Para mantener sus palabras clave en esta fecha ",
    "new-pricing-link": "suscríbase a una oferta de {count} palabras clave",
    "new-pricing-supp": "a {price} €/mes (para un primer pago anual con el cupón BYEBETA)."
  },
  "fr": {
    "add-keywords": "Vous pouvez ajouter jusqu'à ",
    "edit-tags": "Modifier les tags",
    "increase-keywords": "mots clés. Cliquez ici pour augmenter cette limite.",
    "manage-tags": "Tags",
    "notes": "Notes",
    "subscribe-now": "Souscrivez à une offre supérieure pour augmenter cette limite",
    "subscription-limit-reached": "Votre compte contient plus de mots clés que la limite autorisée par votre souscription. La liste ci-dessous a été tronquée à {count} résultats. Sans mise à jour de votre souscription les mots clés supplémentaires seront définitivement supprimé sous 7 jours.",
    "title": "Suivi des positions",
    "tooltip": "Cet outil vous permet de suivre jour après jour l'évolution de vos sites internet sur une liste de mots clés.",
    "positions-not-updated": "Vos position du jour ne sont pas encore actualisées (cela peut prendre quelques minutes jusqu'à quelques heures).",
    "subscribe-for-positions": "Souscrivez pour bénéficier d'une actualisation prioritaire de vos positions.",
    "new-pricing-note": "<strong>Nos tarifs évoluent</strong> : au 12 décembre 2022 l'offre de lancement contenant 200 mots clés gratuits sera remplacée par une offre d'essai de 10 mots clés. Pour conserver vos mots clés à cette date ",
    "new-pricing-link": "souscrivez à une offre de {count} mots clés",
    "new-pricing-supp": "à {price} €/mois (pour un premier paiement annuel avec le coupon BYEBETA)."
  },
  "it": {
    "add-keywords": "Puoi aggiungere fino a ",
    "edit-tags": "Modifica tag",
    "increase-keywords": "parole chiave. Clicca qui per aumentare questo limite.",
    "manage-tags": "Tag",
    "notes": "Note",
    "subscribe-now": "Abbonati a un'offerta superiore per aumentare questo limite",
    "subscription-limit-reached": "Il tuo account contiene più parole chiave del limite consentito dal tuo abbonamento. La lista sottostante è stata troncata a {count} risultati. Senza aggiornare il tuo abbonamento, le parole chiave aggiuntive verranno eliminate definitivamente entro 7 giorni.",
    "title": "Monitoraggio posizioni",
    "tooltip": "Questo strumento ti permette di seguire giorno dopo giorno l'evoluzione dei tuoi siti web su una lista di parole chiave.",
    "positions-not-updated": "Le tue posizioni giornaliere non sono ancora aggiornate (può richiedere da qualche minuto a qualche ora).",
    "subscribe-for-positions": "Abbonati ora per un aggiornamento prioritario delle tue posizioni",
    "new-pricing-note": "<strong>I nostri abbonamenti stanno cambiando</strong> : il 12 dicembre 2022 l'offerta di lancio da 200 parole chiave gratuite sarà sostituita da un'offerta di prova da 10 parole chiave. Per mantenere le tue parole chiave in questa data ",
    "new-pricing-link": "abbonati a un'offerta da {count} parole chiave",
    "new-pricing-supp": "a {price} €/mese (per un primo pagamento annuale con il coupon BYEBETA)."
  },
  "pt": {
    "add-keywords": "Pode adicionar até ",
    "edit-tags": "Editar tags",
    "increase-keywords": "palavras-chave. Clique aqui para aumentar este limite.",
    "manage-tags": "Tags",
    "notes": "Notas",
    "subscribe-now": "Subscreva uma oferta superior para aumentar este limite",
    "subscription-limit-reached": "A sua conta contém mais palavras-chave do que o limite permitido pela sua subscrição. A lista abaixo foi truncada em {count} resultados. Sem atualizar a sua subscrição, as palavras-chave adicionais serão eliminadas permanentemente em 7 dias.",
    "title": "Monitorização de posições",
    "tooltip": "Esta ferramenta permite-lhe acompanhar dia após dia a evolução dos seus websites numa lista de palavras-chave.",
    "positions-not-updated": "As suas posições diárias ainda não foram atualizadas (pode demorar alguns minutos a algumas horas).",
    "subscribe-for-positions": "Subscreva agora para uma atualização prioritária das suas posições",
    "new-pricing-note": "<strong>As nossas subscrições estão a mudar</strong> : a 12 de dezembro de 2022 a oferta de lançamento de 200 palavras-chave gratuitas será substituída por uma oferta de experimentação de 10 palavras-chave. Para manter as suas palavras-chave nesta data ",
    "new-pricing-link": "subscreva uma oferta de {count} palavras-chave",
    "new-pricing-supp": "a {price} €/mês (para um primeiro pagamento anual com o cupão BYEBETA)."
  },
  "pl": {
    "add-keywords": "Możesz dodać do ",
    "edit-tags": "Edytuj tagi",
    "increase-keywords": "słów kluczowych. Kliknij tutaj, aby zwiększyć ten limit.",
    "manage-tags": "Tagi",
    "notes": "Notatki",
    "subscribe-now": "Zasubskrybuj wyższą ofertę, aby zwiększyć ten limit",
    "subscription-limit-reached": "Twoje konto zawiera więcej słów kluczowych niż limit dozwolony przez Twoją subskrypcję. Poniższa lista została skrócona do {count} wyników. Bez aktualizacji subskrypcji dodatkowe słowa kluczowe zostaną trwale usunięte w ciągu 7 dni.",
    "title": "Śledzenie pozycji",
    "tooltip": "To narzędzie umożliwia śledzenie dzień po dniu ewolucji Twoich stron na liście słów kluczowych.",
    "positions-not-updated": "Twoje dzisiejsze pozycje nie zostały jeszcze zaktualizowane (może to potrwać od kilku minut do kilku godzin).",
    "subscribe-for-positions": "Zasubskrybuj teraz, aby uzyskać priorytetową aktualizację swoich pozycji",
    "new-pricing-note": "<strong>Nasze subskrypcje się zmieniają</strong> : 12 grudnia 2022 r. oferta startowa z 200 darmowymi słowami kluczowymi zostanie zastąpiona ofertą próbną z 10 słowami kluczowymi. Aby zachować swoje słowa kluczowe w tej dacie ",
    "new-pricing-link": "zasubskrybuj ofertę {count} słów kluczowych",
    "new-pricing-supp": "za {price} €/miesiąc (dla pierwszej rocznej płatności z kuponem BYEBETA)."
  }
}
</i18n>
